import React, { useContext, useEffect } from "react";
import {
    ContentSection,
    GridRow,
    GridColumn,
    ClearFiltersButton,
    ListTable,
    Grid,
    Stack,
    DropdownMultiselect,
    SearchBox,
    ensure,
    AutocompleteDropdown,
    DetailsRowProps,
    RenderFunction,
    Theme,
    DatePicker,
    MultiselectSearchDropdown
} from "sprint-common-web";

import { TagElementsList } from "../../../common/components/TagElementsList";
import { HeaderBreadcrumb } from "../../../common/ui/layout/header/header-breadcrumb/HeaderBreadcrumb";
import { PageHeader } from "../../../common/ui/layout/header/PageHeader";
import { OrderAirlinesDataContext } from "../../common/data-providers/OrderAirlinesDataProvider";
import { ordersRoutes } from "../../OrdersRoutes";
import { createOrderListingButtons } from "./createOrderListingButtons";
import { createOrderListingColumns } from "./createOrderListingColumns";
import { useOrderListing } from "./hooks/useOrderListing";
import { useOrderListingFilters } from "./hooks/useOrderListingFilters";
import { isDeliveryFolderMissingWhenRequired, OrderListingItem } from "./OrderListingItem";

export const OrderListing: React.VFC = () => {
    const {
        filters,
        statusOptions,
        sourceOptions,
        airlineOptions,
        systemOptions,
        progressOptions,
        filterDistributorOptions,
        setSort,
        setPage,
        setPageSize,
        setStatuses,
        setSources,
        clearFilters,
        setContent,
        setDistributor,
        setAirlines,
        setSystems,
        setPONumber,
        setFilename,
        setProgresses,
        setDueDateFrom,
        setDueDateTo,
        setStartDateFrom,
        setStartDateTo
    } = useOrderListingFilters();

    const {
        items,
        totalItems,
        isLoading,
        getData,
        setSelectedItems,
        selectedItems,
        mergeListItems,
        removeItemsFromMergeList
    } = useOrderListing();

    const { isLoading: areAirlinesLoading } = useContext(OrderAirlinesDataContext);

    useEffect(() => {
        getData(filters);
    }, [filters]);

    const columns = createOrderListingColumns();

    const buttons = createOrderListingButtons({ filters, setPageSize });

    const renderOrderText = (order: OrderListingItem) => {
        return `${order.pONumber} - ${order.content} - ${order.airline.name}`;
    };

    return (
        <>
            <PageHeader>
                <HeaderBreadcrumb routes={ordersRoutes} />
            </PageHeader>
            <ContentSection header="Orders" buttons={buttons}>
                <Grid>
                    <GridRow>
                        <TagElementsList
                            items={mergeListItems}
                            onItemDismiss={(item: OrderListingItem) => removeItemsFromMergeList([item.id])}
                            renderItemText={renderOrderText}
                            styles={{ root: { paddingBottom: 10 } }}
                        />
                    </GridRow>
                    <GridRow>
                        <GridColumn sm={2}>
                            <SearchBox
                                testId="orders-filters-poNumber"
                                placeholder="PO Number"
                                onChange={setPONumber}
                                value={filters.pONumber}
                            />
                        </GridColumn>

                        <GridColumn sm={2}>
                            <SearchBox
                                testId="orders-filters-content"
                                placeholder="Content"
                                onChange={setContent}
                                value={filters.content}
                            />
                        </GridColumn>

                        <GridColumn sm={2}>
                            <SearchBox
                                testId="orders-filters-filename"
                                placeholder="Filename"
                                onChange={setFilename}
                                value={filters.filename}
                            />
                        </GridColumn>
                        <GridColumn sm={3}>
                            <DropdownMultiselect
                                testId="orders-filters-status"
                                placeholder="Status"
                                selectedKeys={filters.statuses}
                                onChange={setStatuses}
                                options={statusOptions}
                            />
                        </GridColumn>
                        <GridColumn sm={2}>
                            <DropdownMultiselect
                                testId="orders-filters-source"
                                placeholder="Source"
                                selectedKeys={filters.sources}
                                onChange={setSources}
                                options={sourceOptions}
                            />
                        </GridColumn>
                        <GridColumn sm={1}>
                            <Stack horizontalAlign="end">
                                <ClearFiltersButton onClear={clearFilters} isLabeled={true} />
                            </Stack>
                        </GridColumn>
                    </GridRow>
                    <GridRow style={{ paddingTop: 10 }}>
                        <GridColumn sm={2}>
                            <DatePicker
                                testId="orders-filters-start-date-from"
                                placeholder="Start date from"
                                value={filters.startDateFrom}
                                onDateSelect={setStartDateFrom}
                            />
                        </GridColumn>

                        <GridColumn sm={2}>
                            <DatePicker
                                testId="orders-filters-start-date-to"
                                placeholder="Start date to"
                                value={filters.startDateTo}
                                onDateSelect={setStartDateTo}
                            />
                        </GridColumn>

                        <GridColumn sm={2}>
                            <DatePicker
                                testId="orders-filters-due-date-from"
                                placeholder="Due date from"
                                value={filters.dueDateFrom}
                                onDateSelect={setDueDateFrom}
                            />
                        </GridColumn>
                        <GridColumn sm={2}>
                            <DatePicker
                                testId="orders-filters-due-date-to"
                                placeholder="Due date to"
                                value={filters.dueDateTo}
                                onDateSelect={setDueDateTo}
                            />
                        </GridColumn>
                    </GridRow>
                    <GridRow style={{ paddingTop: 10 }}>
                        <GridColumn sm={2}>
                            <DropdownMultiselect
                                testId="orders-filters-progress"
                                placeholder="Progress"
                                selectedKeys={filters.progresses}
                                onChange={setProgresses}
                                options={progressOptions}
                            />
                        </GridColumn>
                        <GridColumn sm={2}>
                            <AutocompleteDropdown
                                testId="orders-filters-distributor"
                                placeholder="Distributor"
                                selectedKey={filters.distributorId}
                                selectedText={filters.distributorName}
                                onChange={setDistributor}
                                onSearch={filterDistributorOptions}
                            />
                        </GridColumn>

                        <GridColumn sm={3}>
                            <MultiselectSearchDropdown
                                options={airlineOptions}
                                placeholder="Airlines"
                                onChange={setAirlines}
                                selectedKeys={filters.airlineIds}
                                disabled={!airlineOptions.length}
                            />
                        </GridColumn>

                        <GridColumn sm={2}>
                            <MultiselectSearchDropdown
                                options={systemOptions}
                                placeholder="Systems"
                                onChange={setSystems}
                                selectedKeys={filters.systemIds}
                                disabled={!filters.airlineIds?.length}
                            />
                        </GridColumn>
                    </GridRow>
                    <GridRow>
                        <GridColumn sm={12}>
                            <ListTable
                                columns={columns}
                                selectedItems={selectedItems}
                                items={items}
                                isLoading={isLoading || areAirlinesLoading}
                                pagination={{
                                    page: filters.page,
                                    pageSize: filters.pageSize,
                                    totalItems,
                                    onPageChange: setPage,
                                    onPageSizeChange: setPageSize
                                }}
                                sort={{ by: filters.sortBy, order: filters.sortOrder }}
                                onSortChange={setSort}
                                onSelectionChange={setSelectedItems}
                                onRenderRow={renderRow}
                                layoutMode="fixedColumns"
                                testId={"order-listing-table"}
                                isStickyModeEnabled={true}
                            />
                        </GridColumn>
                    </GridRow>
                </Grid>
            </ContentSection>
        </>
    );
};

const renderRow = (rowProps?: DetailsRowProps, defaultRender?: RenderFunction<DetailsRowProps>) => {
    ensure(rowProps);
    ensure(defaultRender);

    const item = rowProps.item as OrderListingItem;
    const testProps = {
        "data-testid": "table-row",
        "data-testkey": rowProps.item.id
    };

    if (isDeliveryFolderMissingWhenRequired(item)) {
        return renderCustomRow(rowProps, defaultRender, RowType.Warned);
    }

    if (item.isModified) {
        return renderCustomRow(rowProps, defaultRender, RowType.Modified);
    }

    if (item.isPinned) {
        return renderCustomRow(rowProps, defaultRender, RowType.Pinned);
    }

    return defaultRender({ ...testProps, ...rowProps }, defaultRender);
};

enum RowType {
    Modified = 1,
    Warned = 2,
    Pinned = 3
}

const colorsByRowType = {
    [RowType.Modified]: Theme.palette.blue,
    [RowType.Warned]: Theme.palette.yellow,
    [RowType.Pinned]: Theme.palette.green
};

const renderCustomRow = (
    rowProps: DetailsRowProps,
    defaultRender: RenderFunction<DetailsRowProps>,
    type: RowType
): JSX.Element | null => {
    return defaultRender({
        ...rowProps,
        styles: {
            root: {
                userSelect: "auto",
                borderLeft: `4px solid ${colorsByRowType[type]}`
            }
        }
    });
};
